import {
  V1CreateBookingRequest,
  V1GetBookingRequest,
} from '@ennismore/booking-api-client';
import { V1CreateBookingResponse } from '@ennismore/ows-api-client';
import axios from 'axios';
import { z } from 'zod';

import { v1GetBookingStatusResponseSchema } from '@/api/clients/ohip';
import {
  V1GetBookingPaymentMethodDetailsResponse,
  V1GetBookingPaymentMethodsRequest,
  v1PaymentTiming,
} from '@/api/clients/ohip/models';
import { OWSBookingAPIBaseService } from '@/api/clients/ows';
import { captureException } from '@/logging';

import { CreateBookingError } from '../errors';
import { BookingError } from '../errors/booking.error';
import { BookingModel } from '../models';
import { BookingServiceClient } from './common';

export class OWSBookingServiceClient
  extends OWSBookingAPIBaseService
  implements BookingServiceClient
{
  async getBooking(args: V1GetBookingRequest) {
    try {
      const { data } = await this.client.bookingServiceBookingSummary(
        args.bookingId,
        this.options
      );

      return BookingModel.parse(data);
    } catch (e) {
      throw new BookingError(this.parseError(e));
    }
  }

  async getBookingStatus(args: V1GetBookingRequest) {
    try {
      const { data } = await this.client.bookingServiceGetBookingStatus(
        args.bookingId,
        args.hotelReferenceId,
        this.options
      );

      // This should match 1-1 with the OHIP endpoint
      return v1GetBookingStatusResponseSchema.parse(data);
    } catch (e) {
      throw new BookingError(this.parseError(e));
    }
  }

  async createBooking(
    args: V1CreateBookingRequest
  ): Promise<V1CreateBookingResponse> {
    try {
      const { data } = await this.client.bookingServiceCreateBooking({
        from: args.rooms[0].checkInDate,
        to: args.rooms[0].checkOutDate,
        hotelReferenceId: args.hotelReferenceId,
        rateCode: args.rooms[0].rateCode,
        rooms: args.rooms.map((room) => ({
          roomCode: room.roomCode,
          guest: room.guests,
          twin: false, // TODO: Clean this
        })),
      });

      return data;
    } catch (e) {
      throw new CreateBookingError(this.parseError(e));
    }
  }

  async getBookingPaymentMethodDetails(
    args: V1GetBookingPaymentMethodsRequest
  ): Promise<V1GetBookingPaymentMethodDetailsResponse> {
    try {
      const raw = await axios
        .create({ ...this.options, baseURL: this.credentials.baseUrl })
        .get(`/payment/${args.bookingId}`);

      const response = z
        .object({
          payment: z.object({
            method: v1PaymentTiming,
            secret: z.string(),
            connectedAccountId: z.string().optional(),
            publicKey: z.string(),
            country: z.string(),
          }),
        })
        .parse(raw.data);

      return {
        stripeDetails: {
          paymentIntent: {
            clientSecret: response.payment.secret,
            country: response.payment.country,
            method: response.payment.method,
            publicKey: response.payment.publicKey,
          },
          connectedAccountId: response.payment.connectedAccountId,
        },
      };
    } catch (e) {
      console.log(e);
      throw new BookingError(this.parseError(e));
    }
  }

  /**
   * Add-ons are not supported by OWS hotels.
   */
  async getAvailableAddOns() {
    return [];
  }

  async updateBookingAddOns() {
    captureException(new Error('OWS hotel attempting to update add-ons.'));
    return { status: '' };
  }

  /**
   * Calendar is not supported by OWS hotels.
   */
  async getCalendar() {
    return { dates: [] };
  }
}
